import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, RouterLink, Data } from '@angular/router';
import { MainNavigationService } from '../main-navigation.service';
import { MetaDataService } from '../metadata.service';
import { TeaItemProduct } from '@redirector-service/database-module';
import { CartService } from '../cart.service';
import { CartItem } from '@redirector-service/shared';
import { AlertService } from '../alert.service';
import { trigger, transition, style, animate } from '@angular/animations';
import { FeatureFlagsService } from '../feature-flags.service';
import { NGXLogger } from 'ngx-logger';
import { Subject, takeUntil } from 'rxjs';
import { NgClass, AsyncPipe, CurrencyPipe, NgOptimizedImage } from '@angular/common';
import { SubscribeBlockComponent } from '../subscribe-block/subscribe-block.component';
import { TruncatedMarkdownComponent } from '../truncated-markdown/truncated-markdown.component';
import { MarkdownPipe } from '../markdown.pipe';
import { ImageModifierPipe } from '../image-modifer.pipe';
import { TeaItemResolverData } from './tea-item.resolver';

@Component({
    selector: 'tea-shop-fe-tea-item',
    templateUrl: './tea-item.component.html',
    styleUrls: ['./tea-item.component.css'],
    animations: [
        trigger('fadeAnimation', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('300ms ease-in', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate('300ms ease-out', style({ opacity: 0 }))
            ])
        ])
    ],
    imports: [NgClass, RouterLink, SubscribeBlockComponent, TruncatedMarkdownComponent, AsyncPipe, CurrencyPipe, MarkdownPipe, ImageModifierPipe, NgOptimizedImage]
})
export class TeaItemComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private mainNavigationService = inject(MainNavigationService);
  private metaDataService = inject(MetaDataService);
  private cartService = inject(CartService);
  private alertService = inject(AlertService);
  private featureFlagsService = inject(FeatureFlagsService);
  private logger = inject(NGXLogger);


  teaItemProduct!: TeaItemProduct;// | null = null;
  errors: string[] = [];
  // imageUrl = 'https://placehold.co/600x600?q=';
  imageUrl = 'https://senchatea.retool.com/api/file/1feabd05-4d31-49d2-a178-7fdd081eddb0';
  isZoomed = false;
  transformOrigin = '0% 0%';
  images: any[] = [];
  imageIndex = 0;
  currentTab: string | null = null;
  contentMap: Map<string, string> = new Map();
  tabMap: Map<string, string> = new Map();
  accordionMap: Map<string, string> = new Map();
  cartItem!: CartItem;
  isLightboxMode = true;
  showLightbox = false;
  expandedAccordions: Set<string> = new Set();
  flavourInfographics: any[] = [];
  brewingInfographics: any[] = [];
  currentBrewingImageIndex = 0;

  featureLowStockNotice = false;
  private destroy$ = new Subject<void>();

  ngOnInit() {
    // Subscribe to route data changes instead of using snapshot
    this.route.data.pipe(
      takeUntil(this.destroy$)
    ).subscribe((data) => {
      this.updateComponentData(data);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateComponentData(data: Data) {
    // Reset component state
    this.errors = [];
    
    // Get the resolved data from the route
    const teaItemData: TeaItemResolverData = data['teaItemData'];
    
    if (teaItemData) {
      this.errors = teaItemData.errors;
      this.featureLowStockNotice = teaItemData.featureLowStockNotice;
      
      if (teaItemData.teaItemProduct) {
        this.teaItemProduct = teaItemData.teaItemProduct;
        this.processTeaItemProduct();
      } else {
        this.errors.push('Failed to load tea item product data.');
      }
    } else {
      this.errors.push('Failed to load tea item data.');
    }
  }

  private processTeaItemProduct() {
    this.logger.log(`Tea Item Product: `, this.teaItemProduct);
    
    const images = this.teaItemProduct.teaItemProductImages.sort(
      (left, right) => left.position - right.position
    );
    this.images = images.filter((image) => image.imageType === 'product');
    if (this.images.length > 0) {
      this.setImage(0);
    }
    this.flavourInfographics = images.filter((image) => image.imageType === 'flavour-graphic');
    this.brewingInfographics = images.filter((image) => image.imageType === 'brewing-graphic');
    
    this.contentMap = this.parseMarkdown(this.teaItemProduct.description);
    this.currentTab = this.getTabs()[0];
    
    const teaItem = this.teaItemProduct.teaItem;
    const extraTitle = teaItem.teaType && teaItem.teaRegion ? ` ${teaItem.teaType.name} from ${teaItem.teaRegion.name}` : '';
    this.metaDataService.updateMetadata({
      title: `${this.teaItemProduct.name}${extraTitle} - Sencha Tea`,
      image: this.imageUrl,
    });

    this.cartItem = {
      id: this.teaItemProduct.id || this.teaItemProduct.slug,
      quantity: 1,
      name: this.teaItemProduct.name,
      price: this.teaItemProduct.price,
      imageUrl: this.teaItemProduct.teaItemProductImages.length > 0
        ? this.teaItemProduct.teaItemProductImages[0].url
        : '',
    };
    this.logger.log(`Cart Item (base): `, this.cartItem);
    this.logger.log(`Cart Item (loaded): `, this.cartItem);
  }

  /**
   * Set the image to display
   * @param index 
   */
  setImage(index: number) {
    this.imageIndex = index;
    this.imageUrl = this.images[index].url;
  }

  /**
   * Display the previous image
   */
  previousImage() {
    if (this.imageIndex > 0) {
      this.setImage(this.imageIndex - 1);
    }
  }

  /**
   * Display the next image
   */
  nextImage() {
    if (this.imageIndex < this.images.length - 1) {
      this.setImage(this.imageIndex + 1);
    }
  }

  /**
   * Get the list of tabs
   * @returns
   */
  getTabs(): string[] {
    return Array.from(this.tabMap.keys());
  }

  /**
   * Set the current tab to display
   * @param tab 
   */
  setCurrentTab(tab: string) {
    this.currentTab = tab;
  }

  /**
   * Get the content of the current tab
   * @returns
   */
  getTabContent(tab: string | null = null): string {
    return this.tabMap.get(tab || this.currentTab || '') || '';
  }

  /**
   * Parse the markdown content into a map of headers and text blocks
   * @param markdown 
   * @returns
   */
  parseMarkdown(markdown: string): Map<string, string> {
    const resultMap = new Map<string, string>();
    const lines = markdown.split('\n');
    let currentHeader: string | null = null;
    let currentText: string[] = [];
    
    for (const line of lines) {
        const trimmedLine = line;

        // Check for Level 1 headers
        if (trimmedLine.startsWith('# ')) {
            // If there's already a current header, save the current text block
            if (currentHeader) {
              this.logger.log(`Current Header: `, currentHeader);
              if (currentHeader.startsWith('Tab: ')) {
                this.tabMap.set(currentHeader.slice(5).trim(), currentText.join('\n'));
              } else if (currentHeader.startsWith('Accordion: ')) {
                this.logger.log(`Accordion: `, currentHeader.slice(11).trim());
                this.accordionMap.set(currentHeader.slice(11).trim(), currentText.join('\n'));
              } else {
                resultMap.set(currentHeader, currentText.join('\n'));
              }
              currentText = []; // Reset for the next block
            }
            // Set the new header
            currentHeader = trimmedLine.slice(2).trim(); // Remove '# '
        } else { // It's markdown, so we keep empty lines too
            currentText.push(line);
        }
    }

    // Handle the last block if any
    if (currentHeader) {
        if (currentHeader.startsWith('Tab: ')) {
          this.tabMap.set(currentHeader.slice(5).trim(), currentText.join('\n'));
        } else if (currentHeader.startsWith('Accordion: ')) {
          this.accordionMap.set(currentHeader.slice(11).trim(), currentText.join('\n'));
        } else {
          resultMap.set(currentHeader, currentText.join('\n'));
        }
    } else if (currentText.length > 0) {
        // If no Level 1 header was found, and there's still text
        resultMap.set('Description', currentText.join('\n'));
    }

    return resultMap;
  }

  /**
   * Get the list of accordions
   * @returns
   */
  getAccordions(): string[] {
    return Array.from(this.accordionMap.keys());
  }

  /**
   * Get the content of an accordion
   * @param accordion 
   * @returns
   */
  getAccordionContent(accordion: string): string {
    return this.accordionMap.get(accordion) || '';
  }

  onImageMouseMove(e: MouseEvent) {
    if (!this.isZoomed) return;
    
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    
    this.transformOrigin = `${x}% ${y}%`;
  }

  onImageMouseEnter() {
    if (!this.isLightboxMode) {
      this.isZoomed = true;
    }
  }

  onImageMouseLeave() {
    this.isZoomed = false;
  }

  maxAllowed() {
    const maxAllowed = this.teaItemProduct?.stock || 0;
    return maxAllowed;
  }

  hasHighStock() {
    return !this.isOutOfStock() && !this.hasLowStock();
  }

  isOutOfStock() {
    return (this.teaItemProduct?.stock || 0) === 0;
  }

  hasLowStock() {
    const stock = this.teaItemProduct?.stock || 0;
    return this.featureLowStockNotice && 
      stock < 10 && 
      stock > 0;
  }

  incrementQuantity() {
    if (this.cartItem.quantity < (this.teaItemProduct?.stock || 0)) {
      this.cartItem.quantity++;
    }
  }

  decrementQuantity() {
    if (this.cartItem.quantity > 1) {
      this.cartItem.quantity--;
    }
  }

  addToCart() {
    if (this.cartItem.quantity > 0) {
      this.cartService.addToCart(this.cartItem);

      this.alertService.show({
        title: `${this.cartItem.name} added to cart!`,
        description: `${this.cartItem.name} (x${this.cartItem.quantity}) has been added to [your cart](/cart).`,
        type: 'cart',
        duration: 5000,
        thumbnailUrl: this.cartItem.imageUrl,
      });
      
      // Optional: Reset quantity after adding to cart
      this.cartItem.quantity = 1;
    }
  }

  toggleZoomMode() {
    this.isLightboxMode = !this.isLightboxMode;
    if (this.isZoomed) {
      this.isZoomed = false;
    }
    if (this.showLightbox) {
      this.showLightbox = false;
    }
  }

  onImageClick() {
    if (this.isLightboxMode) {
      this.showLightbox = true;
    }
  }

  closeLightbox() {
    this.showLightbox = false;
  }

  toggleAccordion(accordion: string) {
    if (this.expandedAccordions.has(accordion)) {
      this.expandedAccordions.delete(accordion);
    } else {
      this.expandedAccordions.add(accordion);
    }
  }

  isAccordionExpanded(accordion: string): boolean {
    return this.expandedAccordions.has(accordion);
  }

  expandAllAccordions() {
    this.getAccordions().forEach(accordion => {
      this.expandedAccordions.add(accordion);
    });
  }

  collapseAllAccordions() {
    this.expandedAccordions.clear();
  }

  hasExpandedAccordions(): boolean {
    return this.expandedAccordions.size > 0;
  }

  toggleAllAccordions() {
    if (this.hasExpandedAccordions()) {
      this.expandedAccordions.clear();
    } else {
      this.getAccordions().forEach(accordion => {
        this.expandedAccordions.add(accordion);
      });
    }
  }

  previousBrewingImage() {
    this.currentBrewingImageIndex = this.currentBrewingImageIndex === 0 
      ? this.brewingInfographics.length - 1 
      : this.currentBrewingImageIndex - 1;
  }

  nextBrewingImage() {
    this.currentBrewingImageIndex = (this.currentBrewingImageIndex + 1) % this.brewingInfographics.length;
  }

  setBrewingImageIndex(index: number) {
    this.currentBrewingImageIndex = index;
  }
}
